/**
 * Pre-bootstrap snapshot + persist of `authData` from the landing URL.
 * Under HashLocationStrategy the fragment *is* the Router URL, so any redirect
 * that runs before Angular boots would otherwise drop the token.
 * `main.ts` calls {@link capturePersistAndStripCustomerAuthHandoffFromWindow}
 * before `bootstrapModule`: capture → write `__auth_customer_storage__` → strip
 * the bar, so a full reload cannot lose the session and the token never enters
 * the Router UrlTree (#11246).
 */

import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from '../../environments/environment';
import { readSavedState, writeSavedState } from '../local-storage.service';

export const CUSTOMER_AUTH_DATA_PARAM = 'authData';

/** Same key {@link CustomerAuthService.tokenCookieName} / saveState uses. */
export const CUSTOMER_AUTH_STORAGE_KEY = '__auth_customer_storage__';

/**
 * The shape the handoff is allowed to install: a session is only a session if
 * it carries a refresh token. `userId` / `email` identify the customer, which
 * is what {@link isSameCustomerSession} compares — everything else is carried
 * through untouched and is only ever read back after the backend has
 * authenticated the token (`checkCurrentSession` refreshes before the session
 * is trusted, and drops a handed-over one when the token belongs to an account
 * other than the one this names, see {@link isJwtOfSessionCustomer}), so no
 * field here drives display on the strength of the URL alone.
 */
export interface AuthHandoffPayload {
  tokens: { refreshToken: string; jwt?: string };
  userId?: number;
  email?: string;
  [key: string]: unknown;
}

/**
 * The field of a stored session that says it was installed from a handoff link
 * and has not been checked yet: the backend has not yet shown that its refresh
 * token belongs to the customer it names ({@link isJwtOfSessionCustomer}).
 * `checkCurrentSession()` runs that check only on a session that carries it,
 * and removes it once the check passes, so it stays across a reload until then.
 * A session the customer signed into on this site never carries it.
 *
 * What a link says about it is never trusted. Every place that installs a
 * handoff sets it to `true` itself ({@link asUncheckedHandoff}), whatever the
 * link holds, so a link that claims "already checked", or a session handed on
 * from a site that did check it, is checked again here.
 */
export const UNCHECKED_HANDOFF_FIELD = 'uncheckedHandoff';

/**
 * A copy of a handoff payload marked {@link UNCHECKED_HANDOFF_FIELD}, ready to
 * store. The mark is written after the link's own fields, so it always wins.
 */
export function asUncheckedHandoff(
  payload: AuthHandoffPayload,
): AuthHandoffPayload {
  return { ...payload, [UNCHECKED_HANDOFF_FIELD]: true };
}

/** Whether a stored session still waits for the check of a handoff. */
export function isUncheckedHandoff(session: object): boolean {
  return (session as Record<string, unknown>)[UNCHECKED_HANDOFF_FIELD] === true;
}

/** Remove the {@link UNCHECKED_HANDOFF_FIELD} mark once the check passed. */
export function clearUncheckedHandoff(session: object): void {
  delete (session as Record<string, unknown>)[UNCHECKED_HANDOFF_FIELD];
}

/**
 * Capture state for the landing URL, held in one object rather than as loose
 * module globals so there is a single thing to reset and nothing else in the
 * module can reach past the accessors below.
 *
 * `rejected` is the last handoff {@link shouldAcceptAuthHandoff} turned away,
 * kept in memory only (never in storage) for {@link takeRejectedAuthHandoff}.
 */
const capture: {
  param: string | null;
  taken: boolean;
  rejected: AuthHandoffPayload | null;
} = {
  param: null,
  taken: false,
  rejected: null,
};

/**
 * Drop one query param without re-serialising through `URLSearchParams`, so
 * surviving values stay byte-identical (`table=A%20B` must not become `A+B`).
 */
export function removeParamFromQueryString(
  query: string,
  param: string,
): string {
  const remaining = query
    .replace(/^[?&]/, '')
    .split('&')
    .filter((pair) => pair && pair.split('=')[0] !== param)
    .join('&');
  return remaining ? `?${remaining}` : '';
}

export function readAuthDataFromHashString(hash: string): string | null {
  const raw = hash.replace(/^#/, '');
  const qAt = raw.indexOf('?');
  if (qAt === -1) {
    return null;
  }
  return new URLSearchParams(raw.slice(qAt + 1)).get(CUSTOMER_AUTH_DATA_PARAM);
}

export function cleanedHashPathWithoutAuthData(hash: string): string | null {
  const raw = hash.replace(/^#/, '');
  const qAt = raw.indexOf('?');
  if (qAt === -1) {
    return null;
  }
  if (!new URLSearchParams(raw.slice(qAt + 1)).has(CUSTOMER_AUTH_DATA_PARAM)) {
    return null;
  }
  const path = raw.slice(0, qAt) || '/';
  const remainingQuery = removeParamFromQueryString(
    raw.slice(qAt + 1),
    CUSTOMER_AUTH_DATA_PARAM,
  );
  return path + remainingQuery;
}

/**
 * Remove `authData` from search + hash via `history.replaceState` (no Router).
 * Safe before Angular boots — payload must already be snapshotted / persisted.
 */
export function stripAuthDataFromAddressBar(): void {
  const hadSearch = new URLSearchParams(window.location.search).has(
    CUSTOMER_AUTH_DATA_PARAM,
  );
  const cleanedHashPath = cleanedHashPathWithoutAuthData(window.location.hash);
  if (!hadSearch && !cleanedHashPath) {
    return;
  }

  const search = hadSearch
    ? removeParamFromQueryString(
        window.location.search,
        CUSTOMER_AUTH_DATA_PARAM,
      )
    : window.location.search;

  const hash = cleanedHashPath ? '#' + cleanedHashPath : window.location.hash;

  history.replaceState(
    history.state,
    '',
    window.location.pathname + search + hash,
  );
}

/**
 * Encode a session for the handoff param: its JSON as UTF-8 bytes, in base64.
 * `btoa` on its own takes Latin-1 text only, so it throws on a Greek or Polish
 * name or address, or an emoji, and the branch would not open at all. Read
 * back by {@link parseAuthDataParam}. Needs no DI, like the rest of this file.
 * The caller still percent-encodes the result for the URL.
 */
export function encodeAuthDataParam(authData: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(authData));
  let binary = '';
  bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
  return btoa(binary);
}

/**
 * The JSON text inside a handoff param, read as UTF-8 to match
 * {@link encodeAuthDataParam}. A link built before that encoder holds Latin-1
 * text from plain `btoa`, which is almost never valid UTF-8, so text that does
 * not decode as UTF-8 is read as Latin-1 instead and such a link still works.
 * Throws on a param that is not base64.
 */
function decodeAuthDataText(authDataParam: string): string {
  const binary = atob(authDataParam);
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(
      Uint8Array.from(binary, (char) => char.charCodeAt(0)),
    );
  } catch {
    return binary;
  }
}

/**
 * Decode a handoff param. `URLSearchParams.get` / hash reader already decoded
 * percent-encoding — do not call `decodeURIComponent` again.
 * @returns the decoded value, or `null` when the param is not valid
 *          base64-encoded JSON. The result is deliberately `unknown`: the URL
 *          is attacker-reachable, so narrowing is {@link isPersistableAuthData}'s
 *          job, not the parser's.
 */
export function parseAuthDataParam(authDataParam: string): unknown {
  try {
    return JSON.parse(decodeAuthDataText(authDataParam));
  } catch {
    return null;
  }
}

/** Type guard: a payload is only installable if it carries a refresh token. */
export function isPersistableAuthData(
  authData: unknown,
): authData is AuthHandoffPayload {
  return (
    !!authData &&
    typeof authData === 'object' &&
    typeof (authData as { tokens?: { refreshToken?: unknown } }).tokens
      ?.refreshToken === 'string'
  );
}

/**
 * The session currently in storage, or null if there is none / it is junk.
 *
 * "Junk" deliberately includes a stored payload without a string
 * `tokens.refreshToken`: that is not a session anyone can keep — `login()`
 * writes the provider payload before sign-in finishes, and
 * `CustomerAuthService.checkCurrentSession` deletes exactly that shape rather
 * than restoring it (card #11561). Reading it as "nothing stored" is therefore
 * right, and it is what lets a first arrival on a branch origin install its
 * handoff: there is no session to protect.
 */
export function readStoredAuthData(): AuthHandoffPayload | null {
  // One reader for the double-encoded wire format, next to its one writer (see
  // local-storage.service readSavedState / writeSavedState).
  const parsed = readSavedState(CUSTOMER_AUTH_STORAGE_KEY);
  return isPersistableAuthData(parsed) ? parsed : null;
}

/** An email in the one form two copies of it are compared in. */
function normalisedEmail(email: unknown): string {
  return typeof email === 'string' ? email.trim().toLowerCase() : '';
}

/**
 * Whether an arriving handoff belongs to the customer already signed in here.
 * Every identifying field the stored session has, its `userId` and its email
 * (compared without case), must be in the handoff with the same value.
 * Unidentifiable payloads are treated as "not the same customer": the safe
 * answer is to keep the session that is already there.
 *
 * This only tells an honest handoff of a *different* customer apart, as on a
 * shared device. Whoever builds a link writes every field in it, so a link can
 * name the customer signed in here and carry another account's refresh token.
 * Asking for both fields means it has to know both. What the backend says has
 * the last word: `checkCurrentSession()` drops a handed-over session whose
 * refreshed JWT belongs to another account ({@link isJwtOfSessionCustomer}).
 *
 * **Every sign-in path stores a `userId`.** The stored session is always a
 * `CustomerSocialLoginResponse`, whose `userId` is required, and all four paths
 * persist that response: Google / Facebook / Apple through `login()` →
 * `storeService.socialLogin`, email+password through `passwordLogin()`,
 * activation through `customerVerification()`, and a restored session through
 * `checkCurrentSession()`, which re-saves the payload it read. `login()` also
 * writes the raw provider payload (no `userId`, no tokens) before sign-in
 * finishes, but that one has no `refreshToken`, so {@link readStoredAuthData}
 * does not report it as a session at all. Specs in
 * `customer-auth.service.spec.ts` ("every sign-in path stores an identifying
 * userId") pin this per path, so a session signed in on this site can always
 * be identified, and the default above never turns a handoff away for want of
 * a `userId`.
 *
 * The reject branch therefore needs a session with a refresh token already in
 * storage. While the backend still accepts that token, `checkCurrentSession()`
 * restores it: the customer arrives signed in as whoever holds this origin,
 * with that customer's profile avatar on screen, which is exactly the
 * shared-device rule of the story ("A stays signed in and B's sign-in is
 * ignored"). Log out revokes every refresh token of the account on the server,
 * so the stored session can also be one that is already dead. Then the backend
 * refuses its refresh, and `checkCurrentSession()` installs the handoff that was
 * turned away ({@link takeRejectedAuthHandoff}), just as a first arrival with no
 * session would, so a dead session left on a shared device does not make the
 * next customer arrive signed out.
 *
 * Both fields must match, so a handoff of the *same* customer is turned away
 * too when its email differs from the stored copy, as after a change of email.
 * `checkCurrentSession()` settles that in the same way. The stored session is
 * that customer's own, so while it still refreshes it stays and the customer
 * arrives signed in. Once the backend refuses it (the customer's own change of
 * email revokes every refresh token), the handoff is installed in its place.
 */
export function isSameCustomerSession(
  existing: AuthHandoffPayload | null,
  incoming: AuthHandoffPayload,
): boolean {
  if (!existing) {
    return true;
  }
  const hasUserId = typeof existing.userId === 'number';
  const existingEmail = normalisedEmail(existing.email);
  if (!hasUserId && !existingEmail) {
    return false;
  }
  return (
    (!hasUserId || existing.userId === incoming.userId) &&
    (!existingEmail || existingEmail === normalisedEmail(incoming.email))
  );
}

/**
 * The claims inside a JWT, or null when it is missing or cannot be read. It
 * only decodes and checks no signature. `new JwtHelperService()` needs no DI,
 * so this works before Angular boots.
 */
function jwtClaims(jwt: unknown): { iat?: unknown; sub?: unknown } | null {
  if (typeof jwt !== 'string' || !jwt) {
    return null;
  }
  try {
    return new JwtHelperService().decodeToken(jwt) ?? null;
  } catch {
    return null;
  }
}

/**
 * When a payload's JWT was issued (its `iat` claim, in seconds), or null when
 * the payload has no JWT or one that cannot be read. No signature is checked,
 * so a link can carry any `iat` it likes: this only orders honest sessions of
 * one customer, such as an old address reopened from the browser history.
 */
function jwtIssuedAt(authData: AuthHandoffPayload): number | null {
  const iat = jwtClaims(authData.tokens?.jwt)?.iat;
  return typeof iat === 'number' ? iat : null;
}

/**
 * Whether a JWT the backend has just issued for a session's refresh token
 * belongs to the customer the session names. The payload of a handoff is
 * written by whoever built the link, so it can pair the name and email of the
 * customer signed in here with another account's refresh token. The JWT the
 * backend issues for that token is the first thing no link can choose: its
 * subject is the account's username, which the backend sets to the account's
 * email at sign-up and on the customer's own change of email. So a handed-over
 * session whose email differs from that subject (case aside), or that names no
 * email at all, is not who it says it is.
 *
 * That only holds for a session that has just come from a link. A store admin
 * can change a customer's email, and the username with it, without ending the
 * customer's sessions, and an older account can have a username that is not its
 * email. A session the customer signed into on this site can therefore name an
 * email that differs from the subject and still be the customer's own. So
 * `checkCurrentSession()` asks this only of a session marked
 * {@link UNCHECKED_HANDOFF_FIELD}, once.
 *
 * A JWT whose subject cannot be read tells nothing either way, so it passes.
 * The backend always issues one that can be read.
 */
export function isJwtOfSessionCustomer(
  session: { email?: unknown },
  jwt: unknown,
): boolean {
  const subject = jwtClaims(jwt)?.sub;
  if (typeof subject !== 'string' || !subject) {
    return true;
  }
  const email = normalisedEmail(session.email);
  return !!email && email === normalisedEmail(subject);
}

/**
 * Whether an arriving handoff of the *same* customer is an older sign-in than
 * the session already stored here (story #11691: "a fresher sign-in of the same
 * customer is accepted", and only that). Log out revokes every refresh token of
 * the account, so an old handoff address reopened from the browser history
 * carries a dead refresh token. Writing it over a newer, working session would
 * sign the customer out on this site at the next refresh.
 *
 * Only says "older" when it can tell: the two refresh tokens differ and both
 * JWTs carry a readable `iat`. Equal refresh tokens are the same sign-in (the
 * refresh endpoint keeps the refresh token and renews only the JWT), so the
 * arriving copy still overwrites, as does a tie or a JWT that is missing or
 * cannot be decoded.
 */
export function isOlderSignInOfSameCustomer(
  existing: AuthHandoffPayload | null,
  incoming: AuthHandoffPayload,
): boolean {
  if (
    !existing ||
    existing.tokens.refreshToken === incoming.tokens.refreshToken
  ) {
    return false;
  }
  const storedIat = jwtIssuedAt(existing);
  const incomingIat = jwtIssuedAt(incoming);
  return storedIat !== null && incomingIat !== null && incomingIat < storedIat;
}

/**
 * Install a handoff payload, unless a *different* customer is already signed
 * in on this origin, or the payload is an older sign-in of the customer who is
 * ({@link isOlderSignInOfSameCustomer}).
 *
 * A URL is enough to install a session, so an unconditional write is session
 * fixation: a prepared link would sign the customer into someone else's
 * account, and a stale link (history entry, bookmark, paste) would silently
 * swap the account the next order and the next loyalty points land on — on a
 * shared phone or an in-store tablet that is a real sequence, not a
 * contrived one. The handover this story needs never hits that case: the
 * branch either has no session yet (first arrival on its origin) or already
 * holds the same customer's, which is the refresh case and must still be
 * overwritten with the newer tokens.
 *
 * Shared by both persist paths (pre-bootstrap here, and
 * `CustomerAuthService.persistAuthDataParam` on store LOADED) so the rule
 * cannot hold on one of them only.
 *
 * A dropped payload is kept in memory for {@link takeRejectedAuthHandoff}, in
 * case the session it lost to turns out to be dead. An accepted one clears it:
 * the newer handoff is the one now stored.
 *
 * @returns false when the payload must be dropped. The caller still strips the
 *          bar in that case — a rejected token must not be left sitting in the
 *          address bar either.
 */
export function shouldAcceptAuthHandoff(authData: AuthHandoffPayload): boolean {
  const stored = readStoredAuthData();
  if (!isSameCustomerSession(stored, authData)) {
    console.warn(
      'Ignoring customer auth handoff: a different customer is already signed in.',
    );
    capture.rejected = authData;
    return false;
  }
  if (isOlderSignInOfSameCustomer(stored, authData)) {
    console.warn(
      'Ignoring customer auth handoff: the session here is a newer sign-in.',
    );
    capture.rejected = authData;
    return false;
  }
  capture.rejected = null;
  return true;
}

/**
 * The handoff {@link shouldAcceptAuthHandoff} last turned away, or null, and
 * forget it, so it gets one chance at most. Only
 * `CustomerAuthService.checkCurrentSession` installs it, and only once the
 * backend has refused the refresh token of the session it lost to. A session
 * that still refreshes always stays.
 */
export function takeRejectedAuthHandoff(): AuthHandoffPayload | null {
  const rejected = capture.rejected;
  capture.rejected = null;
  return rejected;
}

/**
 * The handoff as it is written to storage: marked for the check of whose token
 * it is ({@link asUncheckedHandoff}), unless it carries the session already
 * stored here (the same refresh token, so the same session on the backend) and
 * that session was checked already. Then it keeps the stored session's state
 * and is written without the mark, whatever the link says. Since #10368
 * `checkCurrentSession()` checks the stored session again on every store
 * change, so in the apps (one WebView, one storage key) the multi-store
 * checkout's hop to a branch, which still carries the session in the route,
 * would otherwise run that check on the customer's own session. A link holding
 * someone else's refresh token is never this case.
 */
export function handoffToStore(
  authData: AuthHandoffPayload,
): AuthHandoffPayload {
  const stored = readStoredAuthData();
  if (
    stored &&
    !isUncheckedHandoff(stored) &&
    stored.tokens.refreshToken === authData.tokens?.refreshToken
  ) {
    const checked = { ...authData };
    clearUncheckedHandoff(checked);
    return checked;
  }
  return asUncheckedHandoff(authData);
}

/**
 * Install a handoff payload from the pre-bootstrap path, subject to
 * {@link shouldAcceptAuthHandoff}, marked {@link UNCHECKED_HANDOFF_FIELD} so
 * `checkCurrentSession()` checks whose token it is, unless it carries the
 * session already stored and checked here ({@link handoffToStore}).
 * @returns true if the payload was installed or deliberately dropped — both
 *          allow the bar to be stripped. false only if a wanted write was
 *          refused by storage.
 */
export function persistAuthHandoffPayload(
  authData: AuthHandoffPayload,
): boolean {
  if (!shouldAcceptAuthHandoff(authData)) {
    return true;
  }
  if (!writeSavedState(CUSTOMER_AUTH_STORAGE_KEY, handoffToStore(authData))) {
    console.warn('Could not persist customer auth handoff to localStorage.');
    return false;
  }
  return true;
}

/**
 * Write the captured landing payload through {@link persistAuthHandoffPayload}
 * (same wire format and same overwrite rule as `CustomerAuthService`).
 * @returns true if the bar may be stripped (nothing to keep, dud, dropped, or
 *          wrote OK). false if a valid payload could not be written.
 */
export function persistCapturedAuthDataToStorage(): boolean {
  const param = capture.param;
  if (!param) {
    return true;
  }

  const authData = parseAuthDataParam(param);
  if (!isPersistableAuthData(authData)) {
    return true;
  }

  return persistAuthHandoffPayload(authData);
}

/** Call from `main.ts` before `bootstrapModule`. */
export function captureCustomerAuthHandoffFromWindow(): void {
  if (capture.taken) {
    return;
  }
  capture.taken = true;
  const hashAuth = readAuthDataFromHashString(window.location.hash);
  // The search string is read for links already in the wild only; every
  // producer in the app writes authData to the fragment (see
  // appendAuthDataToHash) precisely so the token never reaches an access log.
  const fromSearch = new URLSearchParams(window.location.search).get(
    CUSTOMER_AUTH_DATA_PARAM,
  );
  capture.param = hashAuth || fromSearch;
}

/**
 * Put `authData` on the hash query of a branch URL, so `HashLocationStrategy`
 * can read it on arrival and the HTTP request to the branch host never carries
 * the token. Shared by every producer — a query-string copy would land in the
 * branch's nginx/CDN access logs.
 */
export function appendAuthDataToHash(url: string, authData: string): string {
  const hashAt = url.indexOf('#');
  const before = hashAt === -1 ? url : url.slice(0, hashAt);
  const rawHash = hashAt === -1 ? '/' : url.slice(hashAt + 1) || '/';
  const hashBody = rawHash.startsWith('/') ? rawHash : `/${rawHash}`;
  const qAt = hashBody.indexOf('?');
  const path = qAt === -1 ? hashBody : hashBody.slice(0, qAt);
  const existing = qAt === -1 ? '' : hashBody.slice(qAt + 1);
  const params = new URLSearchParams(existing);
  params.set(CUSTOMER_AUTH_DATA_PARAM, authData);
  return `${before}#${path}?${params.toString()}`;
}

/**
 * Pull the `authData` value out of an `&authData=…` query fragment as built by
 * `CustomerAuthService.getAuthQueryParams()`, so producers can move it to the
 * hash instead of concatenating it into a search string.
 */
export function readAuthDataFromQueryFragment(
  authQuery: string,
): string | null {
  if (!authQuery) {
    return null;
  }
  const params = new URLSearchParams(
    authQuery.startsWith('&') ? authQuery.slice(1) : authQuery,
  );
  return params.get(CUSTOMER_AUTH_DATA_PARAM) || null;
}

/**
 * Capture → persist to storage → strip the bar (only if persist allows).
 * Keeps the session across a full reload before Angular injects the service.
 * Wrapped so a rare throw (e.g. history.replaceState) cannot white-screen
 * before bootstrap — LOADED `parseAndSetAuthFromUrl` can still retry.
 */
export function capturePersistAndStripCustomerAuthHandoffFromWindow(): void {
  try {
    captureCustomerAuthHandoffFromWindow();
    if (persistCapturedAuthDataToStorage()) {
      stripAuthDataFromAddressBar();
    }
  } catch (e) {
    console.warn('Customer auth handoff capture failed; continuing.', e);
  }
}

/** Landing `authData` payload, or null if the URL had none. */
export function peekLandingAuthHandoff(): string | null {
  return capture.param;
}

export function clearLandingAuthHandoff(): void {
  capture.param = null;
}

/**
 * Test-only: allow a fresh capture between specs. Guarded rather than merely
 * documented — a production caller would re-arm the capture and let a stale
 * `authData` be installed a second time, so in a production build this is an
 * inert no-op however it is reached.
 */
export function resetCustomerAuthHandoffCaptureForTests(): void {
  if (environment.production) {
    return;
  }
  capture.taken = false;
  capture.rejected = null;
  clearLandingAuthHandoff();
}
